/**
 * Per-project view-state persistence (W1-16 app-integration; ARCHITECTURE.md §5.5, §10 `views` store; DESIGN.md §5.13
 * "Not in history: view state").
 *
 * The editor store's per-project view (workspace, active line, pinned lanes and columns, "also show" lines, snap,
 * timeline zoom and scroll, preview filters, tempo edit policy) plus the playhead beat is written through `PersistClient.saveView` as
 * opaque JSON (`ViewState.toJson`, version-tagged, W1-09), so reopening a chart puts the user back where they were:
 *  - a change of any persisted editor field, or of an extra change source (the transport: seek, play, stop), schedules
 *    a save 300 ms after the LAST change (debounce); a change stream that never pauses (scrolling while the timeline
 *    follows the playhead) is still saved at least every 5 s (max wait);
 *  - a snapshot equal to the last one written is not written again (the first snapshot is the restored view, so
 *    opening a chart writes nothing);
 *  - `flush()` writes a pending change at once (closing the chart, the page being hidden or unloaded);
 *  - nothing is written once the editor store shows another project (the session moved on).
 */

import type { Json, Unsubscribe } from '../core/util/types';
import type { ProjectId } from '../core/model/types';
import type { PersistClient } from '../persist/types';
import type { EditorStore, EditorStoreApi, PersistedViewState } from '../state/types';
import { ViewState } from '../state/viewState';

/** Debounce of view-state writes (ARCHITECTURE.md §5.5: 300 ms). */
export const VIEW_SAVE_DEBOUNCE_MS = 300;
/** Longest time a continuously changing view waits for its write. */
export const VIEW_SAVE_MAX_WAIT_MS = 5000;

/** Editor-store fields that are part of the persisted view (PersistedViewState). */
export const PERSISTED_VIEW_FIELDS = [
  'workspace',
  'activeLine',
  'pinnedLanes',
  'pinnedColumns',
  'alsoShow',
  'snap',
  'timeline',
  'filters',
  'tempoPolicy',
] as const satisfies readonly (keyof EditorStore)[];

/** Timer functions (injectable for tests). */
export interface ViewSaveTimers {
  setTimeout(cb: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  now(): number;
}

const DEFAULT_TIMERS: ViewSaveTimers = {
  setTimeout: (cb, ms) => setTimeout(cb, ms),
  clearTimeout: (handle) => {
    clearTimeout(handle as ReturnType<typeof setTimeout>);
  },
  now: () => Date.now(),
};

export interface ViewStatePersistDeps {
  readonly projectId: ProjectId;
  readonly editor: EditorStoreApi;
  readonly persist: Pick<PersistClient, 'saveView'>;
  /** The view to persist (default: the editor store's `snapshotView()`, which reads the playhead provider). */
  readonly snapshot?: () => PersistedViewState;
  /** Further change sources, e.g. `(cb) => transport.onChange(cb)` (playhead moved by a seek or a stop). */
  readonly changes?: ReadonlyArray<(cb: () => void) => Unsubscribe>;
  readonly debounceMs?: number;
  readonly maxWaitMs?: number;
  readonly timers?: ViewSaveTimers;
  /** A snapshot or a write failed (default: console.warn). Saving continues with the next change. */
  readonly onError?: (err: unknown) => void;
}

export interface ViewStatePersist {
  /** Note a change (debounced write). Editor-store changes are noted automatically. */
  schedule(): void;
  /** Write the view now when it differs from the last one written (a pending write is done at once). */
  flush(): void;
  /** A write is scheduled. */
  readonly pending: boolean;
  /** Number of writes made (diagnostics, tests). */
  readonly writes: number;
  /** Stops listening and cancels a pending write (call `flush()` first to keep it). */
  dispose(): void;
}

function viewFieldsChanged(s: EditorStore, prev: EditorStore): boolean {
  for (const k of PERSISTED_VIEW_FIELDS) if (!Object.is(s[k], prev[k])) return true;
  return false;
}

/** Starts persisting the view of `projectId` (call after the editor store restored it: `editor.openProject`). */
export function createViewStatePersist(deps: ViewStatePersistDeps): ViewStatePersist {
  const timers = deps.timers ?? DEFAULT_TIMERS;
  const debounceMs = deps.debounceMs ?? VIEW_SAVE_DEBOUNCE_MS;
  const maxWaitMs = Math.max(debounceMs, deps.maxWaitMs ?? VIEW_SAVE_MAX_WAIT_MS);
  const onError =
    deps.onError ??
    ((err: unknown) => {
      console.warn('view state: could not save the view of the chart', err);
    });
  const snapshot = deps.snapshot ?? (() => deps.editor.getState().snapshotView());

  let timer: unknown = null;
  /** When the first change of the pending run was noted (max-wait anchor). */
  let firstAt: number | null = null;
  let disposed = false;
  let writes = 0;

  const ownsEditor = (): boolean => deps.editor.getState().projectId === deps.projectId;

  const encode = (): string | null => {
    try {
      return JSON.stringify(ViewState.toJson(snapshot()));
    } catch (err) {
      onError(err);
      return null;
    }
  };

  /** JSON of the last view written (or restored). */
  let last: string | null = ownsEditor() ? encode() : null;

  const cancelTimer = (): void => {
    if (timer !== null) timers.clearTimeout(timer);
    timer = null;
  };

  const write = (): void => {
    cancelTimer();
    firstAt = null;
    if (disposed || !ownsEditor()) return;
    const json = encode();
    if (json === null || json === last) return;
    try {
      deps.persist.saveView(deps.projectId, JSON.parse(json) as Json);
      last = json;
      writes++;
    } catch (err) {
      onError(err);
    }
  };

  const schedule = (): void => {
    if (disposed) return;
    const now = timers.now();
    firstAt ??= now;
    cancelTimer();
    const wait = Math.max(0, Math.min(debounceMs, firstAt + maxWaitMs - now));
    timer = timers.setTimeout(write, wait);
  };

  const offs: Unsubscribe[] = [
    deps.editor.subscribe((s, prev) => {
      if (s.projectId === deps.projectId && viewFieldsChanged(s, prev)) schedule();
    }),
  ];
  for (const source of deps.changes ?? []) offs.push(source(schedule));

  return {
    schedule,
    flush() {
      write();
    },
    get pending() {
      return timer !== null;
    },
    get writes() {
      return writes;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      cancelTimer();
      for (const off of offs.splice(0)) off();
    },
  };
}
